<script>
import SettingsSection from '@/components/advancedSettings/SettingsSection.vue'
import { computed, ref } from '@vue/composition-api'
import { ADVANCED_SETTINGS_OPTIONS } from '@/constants/advancedSettings'
import SpOptimal from '@/assets/helpImages/SpOptimal.png'
import SpPinwheel from '@/assets/helpImages/SpPinwheel.png'
import SpFliplayer from '@/assets/helpImages/SpFliplayer.png'
import SpBrick from '@/assets/helpImages/SpBrick.png'

export default {
  name: 'StabilityPatterns',
  components: { SettingsSection },
  props: ['value'],

  data() {
    return {
      SpOptimal,
      SpPinwheel,
      SpFliplayer,
      SpBrick,
      stabilityPatternOptions:
        ADVANCED_SETTINGS_OPTIONS.STABILITY_PATTERNS_OPTIONS,
    }
  },

  setup(props, { emit }) {
    const stabilityPatternsOptions = ref(
      Object.values(ADVANCED_SETTINGS_OPTIONS.STABILITY_PATTERNS_OPTIONS)
    )

    const updateStabilityPattern = (value) => {
      emit('input', { ...props.value, stability_pattern: value })
    }

    const updateMinUtilForLayers = (value) => {
      emit('input', { ...props.value, min_util_for_layers: value })
    }

    const updateLoadInLayers = (value) => {
      emit('input', { ...props.value, load_in_layers: value })
    }

    const stabilityPattern = computed(() => props.value.stability_pattern)
    const minUtilForLayers = computed(() => props.value.min_util_for_layers)
    const loadInLayers = computed(() => !!props.value.load_in_layers)

    return {
      stabilityPatternsOptions,
      stabilityPattern,
      minUtilForLayers,
      loadInLayers,
      updateStabilityPattern,
      updateMinUtilForLayers,
      updateLoadInLayers,
    }
  },
}
</script>

<template>
  <settings-section
    :hide-switch="true"
    hint-title="Stability patterns"
    label="Stability patterns"
  >
    <template #content>
      <div class="content">
        <div class="examples">
          <div class="exampleFormat">
            <img class="imageSize" :src="SpOptimal" alt="Optimal" />
            Optimal
          </div>
          <div class="exampleFormat">
            <img class="imageSize" :src="SpPinwheel" alt="Pinwheel" />
            Pinwheel
          </div>
          <div class="exampleFormat">
            <img class="imageSize" :src="SpBrick" alt="Brick" />
            Brick
          </div>
          <div class="exampleFormat">
            <img class="imageSize" :src="SpFliplayer" alt="Fliplayer" />
            Fliplayer
          </div>
        </div>
        <div class="description">
          <p><b>Stability Patterns</b></p>
          <p>
            There are 4 available stability patterns. Especially for orders with
            single products, these patterns can be used to create the optimal
            stability and load.
          </p>
          <p>
            For orders with mixed products, the default option is the optimal
            pattern.
          </p>
        </div>
      </div>
    </template>
    <v-select
      :value="stabilityPattern"
      label="Pattern"
      :items="stabilityPatternsOptions"
      item-text="label"
      item-value="value"
      disabled
      @input="$emit('input', $event)"
    />

    <div class="pictureRow">
      <div
        class="sp-choice"
        :class="{
          spSelected:
            stabilityPattern === stabilityPatternOptions.OPTIMAL.value,
        }"
        @click="updateStabilityPattern(stabilityPatternOptions.OPTIMAL.value)"
      >
        <img class="imageSize" :src="SpOptimal" alt="Optimal" />
      </div>
      <div
        class="sp-choice"
        :class="{
          spSelected:
            stabilityPattern === stabilityPatternOptions.PINWHEEL.value,
        }"
        @click="updateStabilityPattern(stabilityPatternOptions.PINWHEEL.value)"
      >
        <img class="imageSize" :src="SpPinwheel" alt="Pinwheel" />
      </div>
      <div
        class="sp-choice"
        :class="{
          spSelected: stabilityPattern === stabilityPatternOptions.BRICK.value,
        }"
        @click="updateStabilityPattern(stabilityPatternOptions.BRICK.value)"
      >
        <img class="imageSize" :src="SpBrick" alt="Brick" />
      </div>
      <div
        class="sp-choice"
        :class="{
          spSelected:
            stabilityPattern === stabilityPatternOptions.FLIPLAYER.value,
        }"
        @click="updateStabilityPattern(stabilityPatternOptions.FLIPLAYER.value)"
      >
        <img class="imageSize" :src="SpFliplayer" alt="Fliplayer" />
      </div>
    </div>

    <div>
      <v-checkbox
        :input-value="loadInLayers"
        label="Load in layers"
        @change="updateLoadInLayers"
      />
      <v-text-field
        type="number"
        :value="minUtilForLayers"
        :step="1"
        :min="0"
        label="Minimum util needed for layers"
        @input="updateMinUtilForLayers"
      />
    </div>
  </settings-section>
</template>

<style scoped lang="scss">
.imageSize {
  max-width: 110px;
}

.pictureRow {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: baseline;
}

.content {
  padding: 1rem;
}

.exampleFormat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.examples {
  display: flex;
  border: black solid 1px;

  div {
    &:not(&:last-child) {
      border-right: black solid 1px;
    }

    box-sizing: border-box;
    display: flex;
    justify-content: center;
    flex: 1;
  }
}

.description {
  border: black solid 1px;
  border-top: none;
  border-radius: 0 !important;
  padding: 1rem;
}

.sp-choice {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.7;
  user-select: none;

  &.spSelected {
    outline: 3px solid #425563;
    border-radius: 4px;
    margin-bottom: 20px;
    opacity: 1;
  }
}

.sp-choice:hover {
  opacity: 1;
}

.btnStyle {
  background-color: orange;
}

table {
  width: 100%;
  border-collapse: collapse;
}

td {
  border: 1px solid black;
}
</style>
